/**
 * Hook for real-time lobby updates via WebSocket.
 *
 * Subscribes to lobby:subscribe via the WS store. Fetches the initial
 * game list immediately via REST, then relies on WS for updates.
 */

import { useEffect, useRef } from "react";
import type { RouteMode } from "../../router";
import type { GameRoom } from "../game-client/types";
import type { useLobbyActions } from "../store/lobby-store";
import { wsActions } from "../store/ws-store";

export function useLobbyWS(
  gameType: string,
  lobbyActions: ReturnType<typeof useLobbyActions>,
  routeMode: RouteMode = "v2",
): void {
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useEffect(() => {
    void routeMode;
    // Fetch initial list immediately via REST
    lobbyActionsRef.current.fetchGames();

    const handleLobbyUpdate = (games: GameRoom[]) => {
      lobbyActionsRef.current.setGames(games);
    };

    const unsubscribe = wsActions.subscribeLobby(gameType, handleLobbyUpdate);

    return () => {
      unsubscribe();
    };
  }, [gameType, routeMode]);
}
